import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Plus, Trash2, FlaskConical, Box as BoxIcon, Folder, FolderOpen, ChevronRight } from 'lucide-react';
import ModelViewer from '../components/ModelViewer';
import { ActionBtn, MoveButtons, moveItem } from '../components/ReorderControls';
import { fileUrl, textureMaps, modelStyle, saveLooks } from '../lib/api';
import { pruneLooks, toggleActive } from '../lib/looks';
import { MarkersSwitch } from './StyleEditor';
import SwapEditor from './SwapEditor';
import { useEscape } from '../hooks/useEscape';

const newId = () => `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export default function LooksEditor({ it, onClose, onSaved }) {
  const [parts, setParts] = useState(null); // null = still scanning the file
  const [draft, setDraft] = useState(null);
  const [testing, setTesting] = useState([]);
  const [markers, setMarkers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [previewItem] = useState(() => ({ id: it.id, modelUrl: fileUrl(it.filename), textures: textureMaps(it.textures), ...modelStyle(it) }));
  useEscape(onClose);

  const onParts = useCallback((list) => {
    setParts((prev) => prev || list);
    setDraft((prev) => prev || pruneLooks(it.looks, list));
  }, [it.looks]);

  const setToggles = (fn) => setDraft((d) => ({ ...d, toggles: fn(d.toggles) }));
  const patchToggle = (i, p) => setToggles((ts) => ts.map((t, j) => (j === i ? { ...t, ...p } : t)));
  const setDefault = (name, v) => setDraft((d) => ({ ...d, defaults: { ...d.defaults, [name]: v } }));
  const addToggle = () => setToggles((ts) => [...ts, { id: newId(), name: '', group: '', show: [], hide: [], swaps: [] }]);

  const save = async () => {
    if (draft.toggles.some((t) => !t.name.trim())) { setErr('Every toggle needs a name.'); return; }
    setBusy(true);
    setErr(null);
    try {
      await saveLooks(it.id, { ...draft, toggles: draft.toggles.map((t) => ({ ...t, name: t.name.trim() })) });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail?.toString() || 'Save failed');
      setBusy(false);
    }
  };

  const target = document.getElementById('tv-screen-root') || document.body;
  return createPortal(
    <div className="absolute inset-0 z-[100] flex items-stretch sm:items-center justify-center bg-[#0a0502]/94 backdrop-blur-sm p-2 sm:p-5" onClick={onClose}>
      <div className="relative w-full max-w-6xl max-h-full flex flex-col rounded-lg border-2 border-[#3a2512] bg-[#1a0e05] lightbox-in"
           onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Looks for ${it.title}`} data-testid="looks-editor">
        <div className="flex items-center justify-between gap-3 p-3 sm:p-4 border-b border-[#3a2512]">
          <div className="min-w-0">
            <div className="font-osd text-[11px] text-[#f5a623] tracking-widest">// LOOKS · OUTFIT TOGGLES</div>
            <div className="font-display text-lg sm:text-2xl text-[#ffe9b8] truncate">{it.title}</div>
          </div>
          <ActionBtn onClick={onClose} label="Close" testId="looks-editor-close"><X className="w-5 h-5" /></ActionBtn>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-4">
          <div className="h-[200px] sm:h-[300px] md:h-[420px] sticky -top-3 sm:-top-4 z-10 rounded-lg overflow-hidden border-2 border-[#3a2512]" data-testid="looks-editor-preview">
            <ModelViewer item={previewItem} expanded fillContainer looks={draft} activeLooks={testing} onParts={onParts} showLights={markers} />
            <MarkersSwitch on={markers} onChange={setMarkers} />
          </div>
          <div className="min-w-0 space-y-5">
            {parts === null && <Note>// SCANNING MODEL FOR PARTS…</Note>}
            {parts?.length === 0 && <Note>NO NAMED PARTS FOUND. EXPORT EACH VARIANT AS ITS OWN NAMED OBJECT IN THE .GLB.</Note>}
            {parts?.length > 0 && draft && (
              <>
                <DefaultLook parts={parts} defaults={draft.defaults} onChange={setDefault} />
                <section className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-osd text-xs text-[#f5a623] tracking-widest">// TOGGLES <span className="text-[#8b6a3e]">· LOWER WINS OVERLAPS</span></div>
                    <ActionBtn onClick={addToggle} label="Add toggle" testId="looks-add-toggle" tone="accent"><Plus className="w-4 h-4" /> ADD TOGGLE</ActionBtn>
                  </div>
                  {draft.toggles.length === 0 && <Note>NO TOGGLES YET. ADD ONE, NAME IT (E.G. "MASK") AND PICK PARTS TO SHOW AND HIDE.</Note>}
                  {draft.toggles.map((t, i) => (
                    <ToggleCard key={t.id} t={t} index={i} total={draft.toggles.length} parts={parts} item={it}
                                testing={testing.includes(t.id)}
                                onTest={() => setTesting((x) => toggleActive(draft.toggles, x, t))}
                                onPatch={(p) => patchToggle(i, p)}
                                onMove={(idx, dir) => setToggles((ts) => moveItem(ts, idx, dir))}
                                onDelete={() => { setToggles((ts) => ts.filter((x) => x.id !== t.id)); setTesting((x) => x.filter((y) => y !== t.id)); }} />
                  ))}
                </section>
              </>
            )}
          </div>
        </div>

        <div className="p-3 sm:p-4 border-t border-[#3a2512] flex items-center justify-between gap-2 flex-wrap">
          <div className="font-osd text-xs text-[#ff6a3a]" role="alert" data-testid="looks-editor-error">{err}</div>
          <div className="flex gap-2 ml-auto">
            <ActionBtn onClick={onClose} label="Cancel" testId="looks-editor-cancel">CANCEL</ActionBtn>
            <ActionBtn onClick={save} disabled={busy || !draft} label="Save looks" tone="accent" testId="looks-editor-save">
              <Save className="w-4 h-4" /> {busy ? 'SAVING…' : 'SAVE'}
            </ActionBtn>
          </div>
        </div>
      </div>
    </div>,
    target
  );
}

function DefaultLook({ parts, defaults, onChange }) {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (name) => setOpen((s) => { const n = new Set(s); if (n.has(name)) n.delete(name); else n.add(name); return n; });
  // Show a part only when every folder above it is open.
  const rows = [];
  const stack = [];
  parts.forEach((p, i) => {
    while (stack.length > p.depth) stack.pop();
    const hasKids = parts[i + 1]?.depth > p.depth;
    if (stack.every((s) => open.has(s))) rows.push({ p, i, hasKids });
    stack.push(p.name);
  });
  return (
    <section className="space-y-2">
      <div className="font-osd text-xs text-[#f5a623] tracking-widest">// DEFAULT LOOK <span className="text-[#8b6a3e]">· TICKED PARTS SHOW WHEN THE MODEL OPENS</span></div>
      <ul className="max-h-[50vh] overflow-y-auto border-2 border-[#3a2512] rounded-lg bg-[#0f0704] divide-y divide-[#2a1608]" data-testid="looks-parts-list">
        {rows.map(({ p, i, hasKids }) => {
          const on = defaults[p.name] ?? p.visible;
          const Icon = p.mesh ? BoxIcon : (open.has(p.name) ? FolderOpen : Folder);
          const isOpen = open.has(p.name);
          return (
            <li key={p.name} className="flex items-center pr-3 hover:bg-[#1a0e05]" style={{ paddingLeft: 4 + Math.min(p.depth, 6) * 16 }}>
              {hasKids ? (
                <button type="button" onClick={() => toggle(p.name)} aria-expanded={isOpen} aria-label={`${isOpen ? 'Close' : 'Open'} ${p.name}`}
                        className="w-8 h-11 shrink-0 flex items-center justify-center text-[#f5a623] hover:text-[#ffe9b8]" data-testid={`looks-folder-${i}`}>
                  <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                </button>
              ) : <span className="w-8 shrink-0" />}
              <label className="flex-1 min-w-0 flex items-center gap-2.5 min-h-[44px] cursor-pointer">
                <input type="checkbox" checked={on} onChange={(e) => onChange(p.name, e.target.checked)}
                       className="w-5 h-5 accent-[#f5a623] shrink-0" data-testid={`looks-default-${i}`} />
                <Icon className={`w-4 h-4 shrink-0 ${p.mesh ? 'text-[#f5a623]' : 'text-[#8b6a3e]'}`} aria-hidden />
                <span className={`font-osd text-xs tracking-wider truncate ${on ? 'text-[#ffe9b8]' : 'text-[#8b6a3e] line-through'}`} title={p.name}>{p.name}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ToggleCard({ t, index, total, parts, item, testing, onTest, onPatch, onMove, onDelete }) {
  const add = (list, name) => {
    const other = list === 'show' ? 'hide' : 'show';
    onPatch({ [list]: [...t[list], name], [other]: t[other].filter((n) => n !== name) });
  };
  const remove = (list, name) => onPatch({ [list]: t[list].filter((n) => n !== name) });

  return (
    <div className={`p-3 border-2 rounded-lg bg-[#0f0704] space-y-3 ${testing ? 'border-[#f5a623]' : 'border-[#3a2512]'}`} data-testid={`looks-toggle-card-${index}`}>
      <div className="flex items-center gap-2">
        <input value={t.name} onChange={(e) => onPatch({ name: e.target.value })} maxLength={24} placeholder="Toggle name, e.g. MASK"
               aria-label="Toggle name" className="flex-1 min-w-0 bg-[#1a0e05] border border-[#3a2512] text-[#ffe9b8] font-osd text-sm tracking-wider uppercase px-2.5 py-2 rounded"
               data-testid={`looks-name-${index}`} />
        <button type="button" onClick={onTest} aria-pressed={testing} data-testid={`looks-test-${index}`}
                className={`header-tab shrink-0 h-11 px-3 font-osd text-xs tracking-widest border-2 rounded-sm flex items-center gap-1.5 ${testing ? 'header-tab-active' : ''}`}>
          <FlaskConical className="w-4 h-4" /> TEST
        </button>
      </div>
      <PartPicker label="SHOW WHEN ON" tone="show" names={t.show} parts={parts} onAdd={(n) => add('show', n)} onRemove={(n) => remove('show', n)} testId={`looks-show-${index}`} />
      <PartPicker label="HIDE WHEN ON" tone="hide" names={t.hide} parts={parts} onAdd={(n) => add('hide', n)} onRemove={(n) => remove('hide', n)} testId={`looks-hide-${index}`} />
      <SwapEditor item={item} swaps={t.swaps || []} onChange={(swaps) => onPatch({ swaps })} index={index} />
      <label className="flex items-center gap-2 flex-wrap">
        <span className="font-osd text-[11px] tracking-widest text-[#8b6a3e]">PICK-ONE GROUP</span>
        <input value={t.group || ''} onChange={(e) => onPatch({ group: e.target.value.toUpperCase() })} maxLength={24} placeholder="e.g. OUTFIT COLOUR (optional)"
               className="flex-1 min-w-[140px] h-9 bg-[#1a0e05] border border-[#3a2512] text-[#ffe9b8] font-osd text-xs px-2 rounded uppercase" data-testid={`looks-group-${index}`} />
        <span className="w-full font-osd text-[10px] text-[#8b6a3e] tracking-wider">TOGGLES WITH THE SAME GROUP NAME TURN EACH OTHER OFF. LEAVE BLANK TO MIX FREELY.</span>
      </label>
      <div className="pt-2.5 border-t border-[#2a1608] flex items-center justify-between gap-2 flex-wrap">
        <MoveButtons index={index} total={total} onMove={onMove} label={t.name || 'toggle'} upTestId={`looks-up-${index}`} downTestId={`looks-down-${index}`} />
        <ActionBtn onClick={onDelete} label={`Delete ${t.name || 'toggle'}`} tone="danger" testId={`looks-delete-${index}`}><Trash2 className="w-4 h-4" /></ActionBtn>
      </div>
    </div>
  );
}

function PartPicker({ label, tone, names, parts, onAdd, onRemove, testId }) {
  const available = parts.filter((p) => !names.includes(p.name));
  const chip = tone === 'show' ? 'border-[#9dff7a]/50 text-[#c8ffb0]' : 'border-[#ff6a3a]/50 text-[#ffb59a]';
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className={`font-osd text-[11px] tracking-widest ${tone === 'show' ? 'text-[#9dff7a]' : 'text-[#ff8a5c]'}`}>{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {names.map((n) => (
          <span key={n} className={`inline-flex items-center gap-1 pl-2 pr-1 h-8 border rounded font-osd text-[11px] bg-[#1a0e05] max-w-full ${chip}`}>
            <span className="truncate">{n}</span>
            <button type="button" onClick={() => onRemove(n)} aria-label={`Remove ${n}`} data-testid={`${testId}-remove-${n}`} className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#2a1608]">
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
        <select value="" onChange={(e) => e.target.value && onAdd(e.target.value)} aria-label={`Add part to ${label.toLowerCase()}`}
                className="h-8 max-w-full bg-[#1a0e05] border border-dashed border-[#5a3418] text-[#d8b887] font-osd text-[11px] px-2 rounded"
                data-testid={`${testId}-add`}>
          <option value="">+ ADD PART…</option>
          {available.map((p) => <option key={p.name} value={p.name}>{'\u2014 '.repeat(Math.min(p.depth, 6))}{p.name}</option>)}
        </select>
      </div>
    </div>
  );
}

function Note({ children }) {
  return <div className="font-osd text-xs text-[#8b6a3e] border-2 border-dashed border-[#5a3418] px-4 py-6 text-center rounded" data-testid="looks-editor-note">{children}</div>;
}
